import React, { useState } from 'react';
import { 
  TableContainer, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Paper, 
  Typography, 
  CircularProgress, 
  IconButton, 
  Tooltip,
  alpha,
  TablePagination,
  Box,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SortableTableCell from './SortableTableCell';

const ServicosTable = ({ 
  servicos, 
  loading, 
  searchTerm, 
  orderBy, 
  orderDirection, 
  handleSort, 
  openEditDialog, 
  handleDeleteServico,
  handleToggleExibirNaEmissao,
  isDarkMode,
  customTheme 
}) => {
  // Estado para controlar a paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Manipuladores para eventos de paginação
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtrar serviços com base no termo de busca
  const filteredServicos = React.useMemo(() => {
    if (!searchTerm) return servicos;
    
    const term = searchTerm.toLowerCase().trim();
    return servicos.filter(servico => (
      servico.nome.toLowerCase().includes(term) ||
      servico.abreviacao.toLowerCase().includes(term) ||
      (servico.descricao && servico.descricao.toLowerCase().includes(term))
    ));
  }, [servicos, searchTerm]);

  // Ordenar serviços
  const sortedAndFilteredServicos = React.useMemo(() => {
    return filteredServicos
      .slice() // Criar uma cópia para não modificar o original
      .sort((a, b) => {
        let valueA = a[orderBy];
        let valueB = b[orderBy];
        
        // Tratamento para valores nulos
        if (valueA === null || valueA === undefined) valueA = '';
        if (valueB === null || valueB === undefined) valueB = '';
        
        // Ordenar strings de forma insensível a case
        if (typeof valueA === 'string' && typeof valueB === 'string') {
          const compareResult = valueA.localeCompare(valueB);
          return orderDirection === 'asc' ? compareResult : -compareResult;
        }
        
        // Ordenar outros tipos de valores
        const compareResult = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
        return orderDirection === 'asc' ? compareResult : -compareResult;
      });
  }, [filteredServicos, orderBy, orderDirection]);

  // Aplicar paginação aos dados
  const paginatedServicos = React.useMemo(() => {
    return sortedAndFilteredServicos.slice(
      page * rowsPerPage, 
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedAndFilteredServicos, page, rowsPerPage]);

  // Estilo comum para células da tabela com padding reduzido
  const cellStyle = { padding: '12px' };

  return (
    <Box>
      <TableContainer 
        component={Paper} 
        elevation={0}
        sx={{ 
          borderRadius: 2, 
          border: `1px solid ${alpha(customTheme.palette.divider, 0.1)}`,
          overflow: 'hidden',
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ bgcolor: theme => alpha(theme.palette.primary.main, isDarkMode ? 0.1 : 0.05) }}>
            <TableRow>
              <SortableTableCell
                column="abreviacao"
                label="Abreviação"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={handleSort}
                sx={cellStyle}
              />
              <SortableTableCell
                column="nome"
                label="Nome do Serviço"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={handleSort}
                sx={cellStyle}
              />
              <TableCell sx={cellStyle}>Descrição</TableCell>
              <TableCell align="center" sx={cellStyle}>Exibir na Emissão</TableCell>
              <TableCell align="right" sx={cellStyle}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                    Carregando serviços...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : sortedAndFilteredServicos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {searchTerm 
                      ? 'Nenhum serviço encontrado com os termos de pesquisa.' 
                      : 'Nenhum serviço cadastrado. Clique em "Novo Serviço" para adicionar.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedServicos.map((servico) => (
                <TableRow 
                  key={servico.id}
                  sx={{ 
                    '&:hover': { 
                      bgcolor: alpha(customTheme.palette.primary.main, 0.04) 
                    } 
                  }}
                >
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold',
                      width: '130px',
                      color: 'primary.main',
                      ...cellStyle
                    }}
                  >
                    {servico.abreviacao}
                  </TableCell>
                  <TableCell sx={cellStyle}>{servico.nome}</TableCell>
                  <TableCell sx={cellStyle}>
                    {servico.descricao || (
                      <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                        Sem descrição
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={cellStyle}>
                    <Tooltip title={servico.exibir_na_emissao ? "Serviço aparece na tela de emissão" : "Serviço não aparece na tela de emissão"}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={servico.exibir_na_emissao || false}
                            onChange={(e) => {
                              if (handleToggleExibirNaEmissao) {
                                handleToggleExibirNaEmissao(servico.id, e.target.checked);
                              }
                            }}
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: customTheme.palette.primary.main,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: customTheme.palette.primary.main,
                              },
                            }}
                          />
                        }
                        label=""
                        sx={{ margin: 0 }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    <Tooltip title="Editar serviço">
                      <IconButton 
                        color="primary" 
                        onClick={() => openEditDialog(servico)}
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir serviço">
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteServico(servico.id)}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Componente de paginação */}
      {!loading && sortedAndFilteredServicos.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={sortedAndFilteredServicos.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{ 
            borderTop: 'none',
            '.MuiTablePagination-selectLabel, .MuiTablePagination-select, .MuiTablePagination-selectIcon, .MuiTablePagination-displayedRows': {
              fontWeight: 500,
            },
          }}
        />
      )}
    </Box>
  );
};

export default ServicosTable; 